import React from 'react';
import { useLocation } from 'react-router-dom';
import { useDocumentProcessingJobs } from '../hooks/useDocumentProcessingJobs';
import { getJobStatusBadgeClass, formatProcessingTime } from '../utils/documentUpload/asyncProcessing';
import { API_BASE_URL } from '../services/api';

/**
 * Professional Document Processing Indicator
 * Enterprise-grade UI with modern design
 * Only visible on document upload and fuel receipts pages
 * Fully responsive for mobile devices
 */
export const DocumentProcessingIndicator: React.FC = () => {
  const location = useLocation();
  const { activeJobs, completedJobs, hasActiveJobs, refreshActiveJobs, loading } = useDocumentProcessingJobs(5000);
  const [showDetails, setShowDetails] = React.useState(false);
  const [deletingJobId, setDeletingJobId] = React.useState<string | null>(null);
  const [retryingJobId, setRetryingJobId] = React.useState<string | null>(null);
  const [clearingAll, setClearingAll] = React.useState(false);

  // Only show on document upload and fuel receipts pages
  const allowedPaths = ['/document-upload', '/fuel-receipts'];
  const isAllowedPage = allowedPaths.includes(location.pathname);

  // Hide indicator when not on allowed pages or when no jobs
  if (!isAllowedPage || (!hasActiveJobs && completedJobs.length === 0)) {
    return null;
  }

  const handleDeleteJob = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    setDeletingJobId(jobId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/documentupload/job/${jobId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete job');
  
      alert('Job deleted successfully');
      await refreshActiveJobs();
    } catch (error: any) {
      alert('Error: ' + (error.message || 'Failed to delete job'));
    } finally {
  setDeletingJobId(null);
    }
  };

  const handleClearAll = async () => {
    if (completedJobs.length === 0) {
      return;
    }

    if (!window.confirm(`Are you sure you want to clear all ${completedJobs.length} completed jobs?`)) {
      return;
  }

    setClearingAll(true);
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
   let failCount = 0;

      // Delete all completed jobs
      for (const job of completedJobs) {
        try {
          const response = await fetch(`${API_BASE_URL}/documentupload/job/${job.jobId}`, {
        method: 'DELETE',
   headers: { Authorization: `Bearer ${token}` },
        });

      if (response.ok) {
            successCount++;
          } else {
            failCount++;
  }
        } catch (error) {
  failCount++;
    }
      }

      if (successCount > 0) {
        alert(`Successfully cleared ${successCount} job(s)${failCount > 0 ? `, failed to clear ${failCount} job(s)` : ''}`);
   await refreshActiveJobs();
      } else {
  alert('Failed to clear jobs');
   }
    } catch (error: any) {
      alert('Error: ' + (error.message || 'Failed to clear jobs'));
    } finally {
      setClearingAll(false);
    }
  };

  const handleRetryJob = async (jobId: string) => {
    setRetryingJobId(jobId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/documentupload/job/${jobId}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to retry job');
      }

      const result = await response.json();
      alert(result.message || 'Job retry started');
      await refreshActiveJobs();
    } catch (error: any) {
    alert('Error: ' + (error.message || 'Failed to retry job'));
    } finally {
      setRetryingJobId(null);
    }
  };

  const totalJobs = activeJobs.length + completedJobs.length;
  const successCount = completedJobs.filter(j => j.status === 'completed').length;
  const failedCount = completedJobs.filter(j => j.status === 'failed').length;

  return (
    <>
      <style>{`
   @keyframes pulse {
   0%, 100% { opacity: 1; }
       50% { opacity: 0.6; }
        }
        @keyframes slideIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
     }
        .processing-indicator-btn {
  transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
     }
        .processing-indicator-btn:hover {
  transform: translateY(-2px);
     box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
    .job-card {
transition: all 0.2s ease;
          border-left: 4px solid transparent;
  }
 .job-card:hover {
      background-color: rgba(0,0,0,0.02);
 border-left-color: #0d6efd;
 }
        .job-card-processing {
        border-left-color: #ffc107;
     background: linear-gradient(90deg, rgba(255,193,7,0.05) 0%, transparent 100%);
 }
     .job-card-completed {
      border-left-color: #198754;
    background: linear-gradient(90deg, rgba(25,135,84,0.05) 0%, transparent 100%);
      }
        .job-card-failed {
  border-left-color: #dc3545;
     background: linear-gradient(90deg, rgba(220,53,69,0.05) 0%, transparent 100%);
        }
        .status-badge {
        padding: 6px 16px;
          border-radius: 20px;
     font-weight: 600;
  font-size: 0.85rem;
          letter-spacing: 0.3px;
        text-transform: uppercase;
      }
        .action-btn {
          min-width: 110px;
    font-weight: 500;
    transition: all 0.2s ease;
   }
     .action-btn:hover {
 transform: translateY(-1px);
   box-shadow: 0 2px 6px rgba(0,0,0,0.15);
        }
        .progress-bar-modern {
   height: 6px;
border-radius: 3px;
  overflow: hidden;
          background: #e9ecef;
    }
        .processing-pulse {
        animation: pulse 2s ease-in-out infinite;
        }
        .slide-in {
          animation: slideIn 0.3s ease-out;
}
        .section-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
   color: white;
    padding: 12px 20px;
          font-weight: 600;
    letter-spacing: 0.5px;
  }
        .stat-card {
    background: white;
   border-radius: 8px;
          padding: 12px 16px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08);
          text-align: center;
   }
        .processing-indicator-container {
    position: fixed;
          top: 70px;
          right: 20px;
    z-index: 1050;
        }
        .processing-details-panel {
       width: 600px;
        max-height: 650px;
          border-radius: 16px;
      border: none;
          overflow: hidden;
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .processing-indicator-container {
            top: 60px;
       right: 10px;
   left: 10px;
    width: auto;
          }
        .processing-indicator-btn {
  width: 100%;
            min-width: auto !important;
        font-size: 0.9rem !important;
 padding: 10px 16px !important;
      }
          .processing-details-panel {
     width: 100% !important;
         max-height: calc(100vh - 140px);
            margin-top: 8px;
          }
  .section-header {
            padding: 10px 16px;
            font-size: 0.95rem;
          }
          .stat-card {
      padding: 8px 12px;
      }
  .stat-card div:first-child {
            font-size: 1.3rem !important;
          }
          .job-card {
   padding: 12px !important;
        }
          .job-card .fw-bold,
      .job-card .fw-semibold {
            font-size: 0.9rem !important;
        }
      .status-badge {
    padding: 4px 12px;
      font-size: 0.75rem;
          }
          .action-btn {
         min-width: auto;
  font-size: 0.85rem;
     padding: 6px 12px;
    }
          .card-footer {
        flex-direction: column;
            gap: 8px;
          }
     .card-footer .d-flex {
 flex-direction: column !important;
            gap: 8px;
          }
          .card-footer button {
        width: 100%;
    }
        }

        /* Extra small devices */
        @media (max-width: 576px) {
          .processing-indicator-container {
  top: 60px;
            right: 5px;
 left: 5px;
  }
  .processing-indicator-btn {
font-size: 0.85rem !important;
  padding: 8px 12px !important;
          }
          .processing-indicator-btn i {
    font-size: 1rem !important;
        }
          .processing-details-panel {
            max-height: calc(100vh - 120px);
        }
          .section-header {
            padding: 8px 12px;
      font-size: 0.9rem;
      }
    .stat-card {
            padding: 6px 8px;
          }
    .stat-card div:first-child {
      font-size: 1.2rem !important;
   }
  .stat-card .small {
     font-size: 0.7rem !important;
      }
          .job-card {
      padding: 10px !important;
    border-left-width: 3px !important;
      }
          .status-badge {
       padding: 3px 10px;
            font-size: 0.7rem;
    }
 .action-btn {
 font-size: 0.8rem;
            padding: 5px 10px;
          }
          .job-card .d-flex.gap-2 {
            flex-direction: column !important;
   }
          .job-card .action-btn {
   width: 100% !important;
       flex-grow: 1 !important;
          }
        }
    `}</style>

      <div className="processing-indicator-container">
  {/* Main Indicator Button */}
    <button
          className={`btn processing-indicator-btn ${hasActiveJobs ? 'btn-warning' : 'btn-success'} btn-lg position-relative`}
  onClick={() => setShowDetails(!showDetails)}
     style={{ 
minWidth: '200px',
       fontSize: '1rem',
       fontWeight: '600',
    borderRadius: '12px',
            padding: '12px 24px'
 }}
        >
          <i className={`bi ${hasActiveJobs ? 'bi-hourglass-split processing-pulse' : 'bi-check-circle-fill'} me-2`} 
       style={{ fontSize: '1.2rem' }}></i>
    <span>
          {hasActiveJobs ? `Processing ${activeJobs.length}` : `${totalJobs} Jobs`}
  </span>
   {hasActiveJobs && (
  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" 
          style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
      {activeJobs.length}
        </span>
          )}
  </button>

        {/* Details Panel */}
        {showDetails && (
   <div 
 className="card shadow-lg mt-3 slide-in processing-details-panel" 
 style={{ 
       width: '600px', 
      maxHeight: '650px', 
   borderRadius: '16px',
            border: 'none',
            overflow: 'hidden'
    }}
 >
      {/* Header */}
        <div className="section-header d-flex justify-content-between align-items-center">
     <div>
        <i className="bi bi-file-earmark-text-fill me-2"></i>
<span style={{ fontSize: '1.1rem' }}>Document Processing</span>
        </div>
   <button
         className="btn btn-link text-white p-0"
 onClick={() => setShowDetails(false)}
style={{ fontSize: '1.3rem', textDecoration: 'none' }}
  >
          <i className="bi bi-x-lg"></i>
       </button>
 </div>

  {/* Statistics Bar */}
   <div className="p-3 bg-light border-bottom">
     <div className="row g-2">
       <div className="col-4">
 <div className="stat-card">
<div className="text-warning fw-bold" style={{ fontSize: '1.5rem' }}>
      {activeJobs.length}
  </div>
       <div className="text-muted small">Processing</div>
    </div>
      </div>
    <div className="col-4">
  <div className="stat-card">
   <div className="text-success fw-bold" style={{ fontSize: '1.5rem' }}>
              {successCount}
     </div>
        <div className="text-muted small">Completed</div>
    </div>
  </div>
      <div className="col-4">
   <div className="stat-card">
      <div className="text-danger fw-bold" style={{ fontSize: '1.5rem' }}>
      {failedCount}
    </div>
        <div className="text-muted small">Failed</div>
    </div>
  </div>
     </div>
 </div>

      {/* Job List */}
            <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
       {/* No Jobs */}
     {!hasActiveJobs && completedJobs.length === 0 && (
   <div className="p-5 text-center text-muted">
     <i className="bi bi-inbox d-block mb-3" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
       <h5 className="fw-light">No Jobs Yet</h5>
            <p className="small">Upload documents to get started</p>
         </div>
       )}

     {/* Active Jobs */}
        {activeJobs.length > 0 && (
    <div className="mb-3">
     <div className="px-3 py-2 bg-warning bg-opacity-10 border-bottom">
     <small className="text-warning fw-bold text-uppercase">
   <i className="bi bi-hourglass-split me-2"></i>
      Active Processing ({activeJobs.length})
   </small>
     </div>
{activeJobs.map(job => (
     <div key={job.jobId} className="job-card job-card-processing p-3 border-bottom">
   <div className="d-flex justify-content-between align-items-start mb-3">
 <div className="flex-grow-1 me-3">
    <div className="fw-bold mb-1" style={{ fontSize: '1rem' }}>
    <i className="bi bi-file-earmark-text me-2 text-primary"></i>
   {job.fileName}
      </div>
   <div className="text-muted small">
      <i className="bi bi-clock me-1"></i>
{formatProcessingTime(job.processingTime)}
 </div>
        </div>
 <div>
    <span className="status-badge bg-warning text-dark">
 <i className="bi bi-arrow-repeat spin me-1"></i>
       Processing
  </span>
   </div>
        </div>
         
<div className="progress-bar-modern mb-3">
      <div className="progress-bar progress-bar-striped progress-bar-animated bg-warning" 
     style={{ width: '100%', height: '100%' }}></div>
    </div>

        <button
     className="action-btn btn btn-outline-danger btn-sm w-100"
onClick={() => handleDeleteJob(job.jobId)}
    disabled={deletingJobId === job.jobId}
  >
           {deletingJobId === job.jobId ? (
            <span className="spinner-border spinner-border-sm me-2"></span>
      ) : (
     <i className="bi bi-trash3 me-2"></i>
      )}
     Cancel Job
 </button>
           </div>
    ))}
</div>
           )}

{/* Completed Jobs */}
         {completedJobs.length > 0 && (
      <div>
 <div className="px-3 py-2 bg-success bg-opacity-10 border-bottom d-flex justify-content-between align-items-center">
    <small className="text-success fw-bold text-uppercase">
      <i className="bi bi-check-circle me-2"></i>
       Recent Jobs ({completedJobs.length})
           </small>
          <button
 className="btn btn-sm btn-outline-danger"
           onClick={handleClearAll}
    disabled={clearingAll}
           title="Clear all completed jobs"
     style={{ fontSize: '0.75rem', padding: '2px 8px' }}
         >
           {clearingAll ? (
    <span className="spinner-border spinner-border-sm me-1"></span>
    ) : (
    <i className="bi bi-trash3 me-1"></i>
   )}
     Clear All
           </button>
  </div>
   {completedJobs.slice(0, 10).map(job => (
     <div key={job.jobId} className={`job-card ${job.status === 'completed' ? 'job-card-completed' : 'job-card-failed'} p-3 border-bottom`}>
     <div className="d-flex justify-content-between align-items-start mb-2">
    <div className="flex-grow-1 me-3">
     <div className="fw-semibold mb-1" style={{ fontSize: '1rem' }}>
      <i className="bi bi-file-earmark-text me-2 text-primary"></i>
  {job.fileName}
          </div>
  <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
       {job.documentType && (
    <span className="badge bg-info-subtle text-info border border-info">
    {job.documentType}
    </span>
 )}
    <small className="text-muted">
   <i className="bi bi-clock me-1"></i>
    {formatProcessingTime(job.processingTime)}
 </small>
      </div>
   {job.result?.recordId && (
   <div className="alert alert-success py-2 px-3 mb-0" style={{ fontSize: '0.9rem' }}>
     <i className="bi bi-check-circle-fill me-2"></i>
      <strong>Record #{job.result.recordId}</strong> created
   </div>
     )}
  {job.errorMessage && (
       <div className="alert alert-danger py-2 px-3 mb-0" style={{ fontSize: '0.9rem' }}>
     <i className="bi bi-exclamation-triangle-fill me-2"></i>
  {job.errorMessage}
      </div>
       )}
      </div>
           <div>
        <span className={`status-badge ${job.status === 'completed' ? 'bg-success-subtle text-success border border-success' : 'bg-danger-subtle text-danger border border-danger'}`}>
   {job.status === 'completed' && <i className="bi bi-check-circle-fill me-1"></i>}
{job.status === 'failed' && <i className="bi bi-x-circle-fill me-1"></i>}
     {job.status}
  </span>
     </div>
     </div>

      <div className="d-flex gap-2 mt-3">
       {job.status === 'failed' && (
          <button
        className="action-btn btn btn-warning btn-sm flex-grow-1"
      onClick={() => handleRetryJob(job.jobId)}
           disabled={retryingJobId === job.jobId}
         >
           {retryingJobId === job.jobId ? (
<span className="spinner-border spinner-border-sm me-2"></span>
 ) : (
<i className="bi bi-arrow-clockwise me-2"></i>
        )}
     Retry
        </button>
    )}
      <button
       className={`action-btn btn btn-outline-danger btn-sm ${job.status === 'failed' ? '' : 'flex-grow-1'}`}
 onClick={() => handleDeleteJob(job.jobId)}
   disabled={deletingJobId === job.jobId}
       >
    {deletingJobId === job.jobId ? (
<span className="spinner-border spinner-border-sm me-2"></span>
      ) : (
     <i className="bi bi-trash3 me-2"></i>
 )}
Delete
        </button>
     </div>
         </div>
    ))}
     </div>
    )}
     </div>

         {/* Footer */}
        <div className="card-footer bg-light border-top-0 py-3">
     <div className="d-flex justify-content-between align-items-center">
   <button
  className="btn btn-primary"
 onClick={refreshActiveJobs}
  disabled={loading}
   style={{ borderRadius: '8px', fontWeight: '500' }}
      >
   <i className={`bi ${loading ? 'bi-arrow-clockwise spinner-border spinner-border-sm' : 'bi-arrow-clockwise'} me-2`}></i>
     {loading ? 'Refreshing...' : 'Refresh Jobs'}
    </button>
          <div className="text-muted small">
Last updated: {new Date().toLocaleTimeString()}
        </div>
         </div>
 </div>
          </div>
 )}
  </div>
    </>
  );
};
